import { Dispatch, useEffect, useRef, useState } from 'react';
import { Button, Flex, Spinner, Text } from '@optiaxiom/react';
import { IconPlus, IconTrashCan } from '@optimizely/axiom-icons';

import type { SpecializedAgentAction } from '@frontend/features/agents/hooks/useSpecializedAgentReducer';
import { uploadFile } from '@frontend/features/agents/services/specialized-agent';
import { useOpalUser } from '@frontend/hooks';
import { AgentFileUrl, SpecializedAgent } from '@frontend/types/specialized-agent';

import { styles } from './AgentFormFiles.css';
import { FilePreview } from './FilePreview';

type FileRowProps = {
  fileName: string;
  fileType: string;
  isUploading: boolean;
  onPreview: () => void;
  onRemove: (e: React.MouseEvent) => void;
  showSpinner?: boolean;
};

function FileRow({
  fileName,
  fileType,
  isUploading,
  onPreview,
  onRemove,
  showSpinner,
}: FileRowProps) {
  return (
    <Flex
      rounded="sm"
      border="1"
      borderColor="border.secondary"
      p="12"
      h="64"
      flexDirection="row"
      w="full"
      bg="bg.default"
      alignItems="center"
      justifyContent="space-between"
      cursor="pointer"
      onClick={onPreview}
    >
      <Flex flexDirection="column" gap="4">
        <Flex flexDirection="row" gap="8" alignItems="center">
          <Text color="fg.default" fontWeight="500">
            {fileName}
          </Text>
          {showSpinner && <Spinner size="sm" />}
        </Flex>
        <Text color="fg.secondary">{fileType.toUpperCase()}</Text>
      </Flex>
      <Button
        appearance="subtle"
        size="sm"
        icon={isUploading ? <Spinner size="md" /> : <IconTrashCan size={16} />}
        onClick={onRemove}
        aria-label="Remove file"
        disabled={isUploading}
      />
    </Flex>
  );
}

type AgentFormFilesProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
};

export function AgentFormFiles({ formData, dispatch }: AgentFormFilesProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { opalUser } = useOpalUser();
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [previewFile, setPreviewFile] = useState<File | AgentFileUrl | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Track created object URLs to revoke them later
  const objectUrlsRef = useRef<Set<string>>(new Set());

  // Get current files from formData.file_urls
  const agentFiles: AgentFileUrl[] = formData.file_urls || [];

  const fileText = 'Attach reference files for your agent here.';

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const currentFiles = formData.file_urls || [];
      for (const file of Array.from(files)) {
        // Prevent duplicates by url (name)
        if (
          currentFiles.some(f => f.url === file.name) ||
          pendingFiles.some(f => f.name === file.name)
        )
          continue;
        setPendingFiles(prev => [...prev, file]);
        setUploadingFiles(prev => [...prev, file.name]);
        try {
          const uploaded = await uploadFile(
            file,
            opalUser.instance.customer_id,
            opalUser.instance.instance_id
          );

          if (uploaded) {
            const previewUrl = URL.createObjectURL(file);
            objectUrlsRef.current.add(previewUrl);
            dispatch({
              type: 'UPDATE_NESTED',
              field: 'file_urls',
              //TODO: this is a hack to get the preview url for the file. backend should return the preview url.
              value: [...(formData.file_urls || []), { ...uploaded, preview_url: previewUrl }],
            });
          }
        } catch (e) {
          console.error('error', e);
        } finally {
          setUploadingFiles(prev => prev.filter(name => name !== file.name));
          setPendingFiles(prev => prev.filter(f => f.name !== file.name));
        }
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const revokeObjectUrl = (url?: string) => {
    if (url && objectUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      objectUrlsRef.current.delete(url);
    }
  };

  const handleRemoveFile = (fileUrl: string) => {
    const fileToRemove = (formData.file_urls || []).find((f: AgentFileUrl) => f.url === fileUrl);
    if (fileToRemove && fileToRemove.preview_url) {
      revokeObjectUrl(fileToRemove.preview_url);
    }
    const updatedFiles = (formData.file_urls || []).filter((f: AgentFileUrl) => f.url !== fileUrl);
    dispatch({
      type: 'UPDATE_NESTED',
      field: 'file_urls',
      value: updatedFiles,
    });
  };

  useEffect(() => {
    const objectUrls = objectUrlsRef.current;
    return () => {
      objectUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
      objectUrls.clear();
    };
  }, []);

  return (
    <>
      <Flex flexDirection="column" gap="8">
        <Flex flexDirection="row" gap="16" justifyContent="flex-end">
          {(agentFiles.length > 0 || pendingFiles.length > 0) && (
            <Button
              appearance="default"
              addonBefore={<IconPlus size="16" />}
              onClick={handleFileSelect}
            >
              Add File
            </Button>
          )}
        </Flex>
        {agentFiles.length > 0 || pendingFiles.length > 0 ? (
          <Flex flexDirection="column" gap="8">
            {pendingFiles.map(file => (
              <FileRow
                key={`pending-${file.name}-${file.lastModified || Date.now()}`}
                fileName={file.name}
                fileType={file.type || 'text'}
                isUploading={uploadingFiles.includes(file.name)}
                onPreview={() => {
                  setPreviewFile(file);
                  setIsPreviewOpen(true);
                }}
                onRemove={e => {
                  e.stopPropagation();
                  setPendingFiles(prev => prev.filter(f => f.name !== file.name));
                }}
              />
            ))}
            {agentFiles.map((file, index) => (
              <FileRow
                key={file.url || `file-${index}`}
                fileName={file.description || file.url}
                fileType={file.type || 'text'}
                isUploading={uploadingFiles.includes(file.url)}
                showSpinner={uploadingFiles.includes(file.url)}
                onPreview={() => {
                  setPreviewFile(file);
                  setIsPreviewOpen(true);
                }}
                onRemove={e => {
                  e.stopPropagation();
                  handleRemoveFile(file.url);
                }}
              />
            ))}
          </Flex>
        ) : (
          <Flex
            className={styles.dashedBorder}
            rounded="sm"
            border="1"
            borderColor="border.secondary"
            w="full"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            bg="bg.secondary"
            p="20"
          >
            <Text color="fg.secondary">{fileText}</Text>
            <Button
              appearance="subtle"
              addonBefore={<IconPlus size="16" />}
              onClick={handleFileSelect}
            >
              Add File
            </Button>
          </Flex>
        )}
      </Flex>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept="*/*"
      />

      {previewFile && (
        <FilePreview file={previewFile} isOpen={isPreviewOpen} setIsOpen={setIsPreviewOpen} />
      )}
    </>
  );
}
